'use client';

import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import type { BrandPreviewSurface } from '@/lib/brand-preview/hyrox-theme';

/**
 * The indicator shown while a brand preview is active. It rests as a small
 * dot in the corner, so it no longer covers page buttons (it sat over "Fill
 * it in" on the hub ticket); a tap opens it to say which preview is on and to
 * exit. On the mobile hub it sits above the bottom nav bar.
 */
export function BrandPreviewPill({ surface }: { surface: BrandPreviewSurface }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const exit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch('/api/brand-preview?clear=1', { method: 'POST' });
    } catch {}
    // Belt and braces: expire the (non-httpOnly) cookie client-side too, so
    // the reload always lands unskinned even if the API call failed.
    document.cookie = 'fv-brand-preview=; path=/; max-age=0';
    window.location.reload();
  };

  const place = {
    position: 'fixed' as const,
    right: '12px',
    bottom: surface === 'hub' && isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 74px)' : '12px',
    // Above page content, below modal/sheet overlays (z-50) so it never
    // covers dialog CTAs.
    zIndex: 40,
  };
  const dot = (
    <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFED00', flexShrink: 0 }} />
  );

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="HYROX preview is on. Open to exit"
        title="HYROX preview"
        style={{
          ...place,
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0B0B0B',
          border: '1px solid rgba(255,237,0,0.5)',
          cursor: 'pointer',
          opacity: 0.85,
          boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
        }}
      >
        {dot}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="HYROX preview"
      style={{
        ...place,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 4px 4px 12px',
        borderRadius: 999,
        background: '#0B0B0B',
        border: '1px solid rgba(255,237,0,0.5)',
        color: '#FFFFFF',
        fontFamily: 'var(--fvd-font-body)',
        fontSize: 12,
        fontWeight: 500,
        boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
      }}
    >
      {dot}
      <span style={{ marginLeft: 4, marginRight: 4 }}>HYROX preview</span>
      <button
        type="button"
        onClick={exit}
        disabled={busy}
        style={{ minHeight: 36, padding: '0 12px', borderRadius: 999, border: 0, background: '#FFED00', color: '#111111', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}
      >
        {busy ? 'Exiting' : 'Exit'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Hide"
        style={{ minHeight: 36, minWidth: 36, borderRadius: 999, border: 0, background: 'transparent', color: 'rgba(255,255,255,0.7)', fontFamily: 'inherit', fontSize: 16, cursor: 'pointer' }}
      >
        ×
      </button>
    </div>
  );
}
